import {
  Box,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { Icons, Images } from "assets";
import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useState } from "react";
import PaginationComponent from "shared/components/pagination";
import RangeDatePicker from "shared/components/rangeDatePicker";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import useDebounce from "shared/customHooks/useDebounce";
import useWindowSize from "shared/customHooks/windowSize";
import BlockRequests from "shared/modals/blockRequest";
import Confirmation from "shared/modals/confirmation";
import { routeConstant } from "shared/routes/routesConstant";
import {
  blockRequests,
  blockUserService,
  rejectUserService,
  unblockUserService,
} from "shared/services/projectHeadService";
import { block_status, user_roles } from "shared/utils/constants";
import {
  author_accounts,
  deactivated_accounts,
  headers_enums,
  publisher_accounts,
  school_accounts,
  user_accounts,
} from "./constants";
import styles from "./style.module.scss";
import AnimationLoader from "shared/components/loader/animation";

function ApprovalRequests() {
  const screenWidth = useWindowSize();
  const [activeTab, setActiveTab] = useState<any>(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [headers, setHeaders] = useState<any>(publisher_accounts);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [allRequests, setAllRequests] = useState<any>([]);
  const [openBlockRequest, setOpenBlockRequest] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openConfirm, setOpenConfirmation] = useState<boolean>(false);
  const [loader, setLoader] = useState<any>(true);
  const [blockLoader, setBlockLoader] = useState<boolean>(false);
  const [action, setAction] = useState<{ label: string; type: string }>({
    label: "",
    type: "",
  });

  const tabs = [
    {
      value: 0,
      role: user_roles?.publisher,
      label: "Publisher Accounts",
    },
    {
      value: 1,
      role: user_roles?.author,
      label: "Author Accounts",
    },
    {
      value: 2,
      role: user_roles?.school,
      label: "School Accounts",
    },
    {
      value: 3,
      role: user_roles?.reader,
      label: "Kid Accounts",
    },
    {
      value: 4,
      role: user_roles?.parent,
      label: "Parent Accounts",
    },
    {
      value: 5,
      role: 7, //this is for deactivate enum set from frontend
      label: "Deactivated Accounts",
    },
  ];

  const handleChange = (event: any, newValue: number) => {
    setActiveTab(newValue);
    setActivePage(1);

    if (newValue === headers_enums.publisher_accounts) {
      setHeaders(publisher_accounts);
    } else if (newValue === headers_enums.author_accounts) {
      setHeaders(author_accounts);
    } else if (newValue === headers_enums.school_accounts) {
      setHeaders(school_accounts);
    } else if (newValue === headers_enums.deactivated_accounts) {
      setHeaders(deactivated_accounts);
    } else if (
      newValue === headers_enums.reader_accounts ||
      newValue === headers_enums.parent_accounts
    ) {
      setHeaders(user_accounts);
    }
  };

  useBreadcrumbs(
    {
      title: "Admin requests",
      path: routeConstant.approveRequest.path,
    },
    true
  );

  const getAllRequests = () => {
    setLoader(true);
    blockRequests(
      10,
      searchText,
      tabs?.find((item: any) => item?.value === activeTab)?.role,
      {
        from: moment(startDate).format("YYYY-MM-DD"),
        to: moment(endDate).format("YYYY-MM-DD"),
      }
    )
      .then(({ data }) => {
        setLoader(false);
        setAllRequests(data?.data?.data);
        setTotalPage(data?.data?.last_page);
      })
      .catch((err) => {
        setLoader(false);
        console.log("data", err?.response?.data?.message);
      });
  };

  useEffect(() => {
    if (startDate && endDate) {
      getAllRequests();
    }
    //eslint-disable-next-line
  }, [activeTab, searchText, activePage, endDate]);

  const handleAction = (action: any, selectedItem: any) => {
    setOpenConfirmation(false);
    setBlockLoader(true);

    const service: any =
      action?.type === "activate"
        ? unblockUserService
        : action?.type === "approve"
        ? selectedItem?.is_blocked === block_status.request_block
          ? blockUserService
          : selectedItem?.is_blocked === block_status.request_unblock
          ? unblockUserService
          : null
        : action?.type === "reject"
        ? rejectUserService
        : null;

    service?.(selectedItem?.id)
      .then((data: any) => {
        console.log("data", data?.data?.message);
        getAllRequests();
        setBlockLoader(false);
        if (data?.data?.status) {
          toastMessage("success", data?.data?.message);
        } else {
          toastMessage("error", data?.data?.message);
        }
      })
      .catch((err: any) => {
        console.log("Error----", err);
        setBlockLoader(false);
        toastMessage("error", err?.response?.data?.message);
      });
  };

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  return (
    <div>
      <div className={styles.topLevelContainer}>
        <div className={styles.headingContainer}>
          <label className={styles.heading}>
            Admin Roles request to activate or deactivate accounts
          </label>
          <label className={styles.subHeading}>
            Here you can reject or approve user accounts
          </label>
        </div>
        <div className={styles.rangePicker}>
          <RangeDatePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={(date: Date | null) => {
              setEndDate(date);
              setActivePage(1);
            }}
            doubleLabel={true}
          />
        </div>
      </div>

      <div className={styles.mainContainer}>
        <Box className={styles.boxContainer}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            className={styles.tab}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: {
                backgroundColor: "black",
              },
            }}
          >
            {tabs?.map((item: any, index: any) => {
              return (
                <Tab
                  label={
                    <div
                      className={classNames(
                        styles.tabs,
                        activeTab === item?.value && styles.active
                      )}
                    >
                      <label className={styles.tabLabel}> {item?.label}</label>
                    </div>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
        <Box className={styles.toptableContainer}>
          <div className={styles.headingContainer}>
            {activeTab === headers_enums.publisher_accounts ? (
              <>
                <label className={styles.tableHeading}>
                  Publishers Accounts
                </label>
              </>
            ) : activeTab === headers_enums.author_accounts ? (
              <>
                <label className={styles.tableHeading}>Authors Accounts</label>
              </>
            ) : activeTab === headers_enums.school_accounts ? (
              <>
                <label className={styles.tableHeading}>Schools Accounts</label>
              </>
            ) : activeTab === headers_enums.deactivated_accounts ? (
              <>
                <label className={styles.tableHeading}>
                  Deactivated Accounts
                </label>
              </>
            ) : activeTab === headers_enums.reader_accounts ? (
              <>
                <label className={styles.tableHeading}>Reader Accounts</label>
              </>
            ) : activeTab === headers_enums.parent_accounts ? (
              <>
                <label className={styles.tableHeading}>Parent Accounts</label>
              </>
            ) : (
              " "
            )}
          </div>
          <div className={styles.gridContainer}>
            <Icons.SearchIcon className={styles.searchIcon} />
            <input
              placeholder="Search"
              className={styles.input}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </Box>

        <TableContainer
          component={Paper}
          className={styles.tableContainer}
          sx={screenWidth > 990 ? { width: `${screenWidth - 300}px` } : {}}
          id="table-view-dashboard"
        >
          <Table aria-label="simple table">
            <TableHead className={styles.tableHead}>
              <TableRow>
                {headers?.map((item: any) => {
                  return (
                    <>
                      <TableCell>{item}</TableCell>
                    </>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {loader ? (
                <Loader activeTab={activeTab} />
              ) : (
                allRequests?.map((item: any, index: any, arr: any) => {
                  return (
                    <TableRow
                      className={classNames(
                        index + 1 === arr.length && styles.lastCells
                      )}
                    >
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                          }}
                        >
                          <img
                            src={
                              item?.role === user_roles.parent
                                ? item?.parent?.profile_picture
                                : item?.role === user_roles.reader
                                ? item?.reader?.profile_picture
                                : item?.role === user_roles.author
                                ? item?.author?.profile_picture
                                : item?.role === user_roles.publisher
                                ? item?.publisher?.profile_picture
                                : item?.role === user_roles.school
                                ? item?.school?.profile_picture
                                : Images.UserPlaceholderImage
                            }
                            alt="profile_picture"
                            className={styles.profilepic}
                          />
                          <label>
                            {" "}
                            {item?.role === user_roles.parent
                              ? item?.parent?.full_name
                              : item?.role === user_roles.reader
                              ? item?.reader?.full_name
                              : item?.role === user_roles.author
                              ? item?.author?.full_name
                              : item?.role === user_roles.publisher
                              ? item?.publisher?.full_name
                              : item?.role === user_roles.school
                              ? item?.school?.full_name
                              : "---"}
                          </label>
                        </div>
                      </TableCell>

                      {activeTab === headers_enums.school_accounts && (
                        <TableCell>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              width: "100%",
                            }}
                          >
                            <label>
                              {item?.role === user_roles.school
                                ? item?.school?.full_name
                                : "---"}
                            </label>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <div>
                          {item?.role === user_roles.parent
                            ? item?.parent?.phone
                            : item?.role === user_roles.reader
                            ? item?.reader?.phone
                            : item?.role === user_roles.author
                            ? item?.author?.phone
                            : item?.role === user_roles.publisher
                            ? item?.publisher?.phone
                            : item?.role === user_roles.school
                            ? item?.school?.phone
                            : "---"}
                        </div>
                      </TableCell>

                      {activeTab === headers_enums.deactivated_accounts && (
                        <>
                          <TableCell className={styles.tableCell}>
                            {item?.role === user_roles.parent
                              ? "Parent"
                              : item?.role === user_roles.reader
                              ? "Kid"
                              : item?.role === user_roles.author
                              ? "Author"
                              : item?.role === user_roles.publisher
                              ? "Publisher"
                              : item?.role === user_roles.school
                              ? "School"
                              : "---"}
                          </TableCell>
                          <TableCell className={styles.tableCell}>
                            {moment(item?.created_at)?.format("D MMMM YYYY")}
                          </TableCell>
                        </>
                      )}

                      {activeTab === headers_enums.deactivated_accounts ? (
                        <TableCell className={classNames(styles.statusCell)}>
                          <label className={styles.rejected}>Deactivated</label>
                        </TableCell>
                      ) : (
                        <TableCell className={classNames(styles.statusCell)}>
                          <label
                            className={classNames(
                              item?.is_blocked === block_status.request_unblock
                                ? styles.neutral
                                : item?.is_blocked ===
                                  block_status.request_block
                                ? styles?.rejected
                                : ""
                            )}
                          >
                            {item?.is_blocked === block_status.request_block
                              ? "Block"
                              : item?.is_blocked ===
                                block_status.request_unblock
                              ? "Unblock"
                              : ""}
                          </label>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className={styles.actionContainer}>
                          <Tooltip
                            componentsProps={{
                              tooltip: { className: "tooltip" },
                              arrow: { className: "arrow" },
                            }}
                            placement="top"
                            arrow
                            title="View Reason"
                          >
                            <div
                              onClick={() => {
                                setSelectedItem(item);
                                setOpenBlockRequest(true);
                              }}
                            >
                              <Icons.EyeIcon className={styles.icons} />
                            </div>
                          </Tooltip>

                          {activeTab !== headers_enums.deactivated_accounts && (
                            <>
                              <Tooltip
                                componentsProps={{
                                  tooltip: { className: "tooltip" },
                                  arrow: { className: "arrow" },
                                }}
                                placement="top"
                                arrow
                                title={"Approve"}
                              >
                                <div
                                  onClick={() => {
                                    setOpenConfirmation(true);
                                    setSelectedItem(item);
                                    setAction({
                                      label:
                                        "Are you sure, you want to approve this request?",
                                      type: "approve",
                                    });
                                  }}
                                >
                                  <Icons.ApproveIcon
                                    className={styles.deactivate}
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                componentsProps={{
                                  tooltip: { className: "tooltip" },
                                  arrow: { className: "arrow" },
                                }}
                                placement="top"
                                arrow
                                title={"Reject"}
                              >
                                <div
                                  onClick={() => {
                                    setOpenConfirmation(true);
                                    setSelectedItem(item);
                                    setAction({
                                      label:
                                        "Are you sure, you want to reject this request?",
                                      type: "reject",
                                    });
                                  }}
                                >
                                  <Icons.RejectIcon
                                    className={styles.activate}
                                  />
                                </div>
                              </Tooltip>
                            </>
                          )}
                          {activeTab === headers_enums.deactivated_accounts && (
                            <Tooltip
                              componentsProps={{
                                tooltip: { className: "tooltip" },
                                arrow: { className: "arrow" },
                              }}
                              placement="top"
                              arrow
                              title={
                                !item?.is_blocked ? "Deactivate" : "Activate"
                              }
                            >
                              <div
                                onClick={() => {
                                  setOpenConfirmation(true);
                                  setSelectedItem(item);
                                  setAction({
                                    label:
                                      "Are you sure, you want to activate this request?",
                                    type: "activate",
                                  });
                                }}
                              >
                                {!item?.is_blocked ? (
                                  <Icons.UserDeactivate
                                    className={classNames(
                                      styles.icons,
                                      styles.deactive
                                    )}
                                  />
                                ) : (
                                  <Icons.UserActivate
                                    className={classNames(
                                      styles.icons,
                                      styles.active
                                    )}
                                  />
                                )}
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loader && !(allRequests?.length > 0) && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "40px",
              flexDirection: "column",
              gap: "24px",
              alignItems: "center",
            }}
          >
            <Icons.NoUsersData />
            <h5>No Data Found</h5>
          </div>
        )}

        {totalPage > 1 && (
          <PaginationComponent
            totalPages={totalPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        )}
      </div>

      <BlockRequests
        open={openBlockRequest}
        handleClose={() => {
          setOpenBlockRequest(false);
          setSelectedItem(null);
        }}
        selectedItem={selectedItem}
      />

      <Confirmation
        open={openConfirm}
        handleClose={() => {
          setAction({
            label: "",
            type: "",
          });
          setOpenConfirmation(false);
          setSelectedItem(null);
        }}
        handleClick={() => handleAction(action, selectedItem)}
        text={action.label}
      />

      {blockLoader && <AnimationLoader />}
    </div>
  );
}

export default ApprovalRequests;

const Loader = ({ activeTab }: any) => {
  return (
    <React.Fragment>
      {Array.from({ length: 2 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton variant="rounded" width={140} height={25} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" width={140} height={25} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" width={140} height={25} />
          </TableCell>
          {activeTab === headers_enums.school_accounts && (
            <TableCell>
              <Skeleton variant="rounded" width={140} height={25} />
            </TableCell>
          )}

          {activeTab === headers_enums.deactivated_accounts && (
            <>
              <TableCell>
                <Skeleton variant="rounded" width={140} height={25} />
              </TableCell>
              <TableCell>
                <Skeleton variant="rounded" width={140} height={25} />
              </TableCell>
            </>
          )}

          <TableCell>
            <Box display={"flex"}>
              <Skeleton width={30} height={40} sx={{ marginRight: "10px" }} />
              <Skeleton width={30} height={40} sx={{ marginRight: "10px" }} />
              <Skeleton width={30} height={40} sx={{ marginRight: "10px" }} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </React.Fragment>
  );
};
